/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject } from '@angular/core';
import { Color, ToastButton } from '@ionic/core';
import { ofType } from '@ngrx/effects';
import { Action, ActionCreator } from '@ngrx/store';
import { withScope } from '@sentry/angular';
import { SentryErrorHandler } from '@techniek-team/sentry-web';
import { ToastService } from '@techniek-team/services';
import { Observable, pipe, tap, UnaryFunction } from 'rxjs';

export interface HandleEndpointToastOptions<ActionType extends ActionCreator> {
  message: string | ((action: ReturnType<ActionType>) => string);

  duration?: number;

  color?: Color;

  cssClass?: string | string[];

  position?: 'top' | 'bottom' | 'middle';

  buttons?: (ToastButton | string)[];
}

// eslint-disable-next-line max-lines-per-function
export function handleEndpointFailure<
  ActionType extends ActionCreator = ActionCreator<
    string,
    (props: { error: unknown }) => { error: unknown } & Action
  >,
>(
  failureAction: ActionType,
  options: HandleEndpointToastOptions<ActionType> = {
    message: 'Er is iets misgegaan. Probeer het opnieuw.',
  },
): UnaryFunction<Observable<Action>, Observable<unknown>> {
  options = {
    duration: 10000,
    color: 'dark',
    buttons: [{ text: 'Sluiten', role: 'cancel' }],
    position: 'bottom',
    ...options,
  };

  const sentryErrorHandler: SentryErrorHandler = inject(SentryErrorHandler);
  const toastService: ToastService = inject(ToastService);
  return pipe(
    ofType(failureAction),
    tap((action: ReturnType<ActionType>) => {
      const message: string =
        typeof options.message === 'string' ? options.message : options.message(action);
      void withScope((scope) => {
        scope.setTag('action', failureAction.type);
        scope.setContext('NGRX', {
          action: (action as Action)['type'],
          payload: action,
        });
        return Promise.all([
          sentryErrorHandler.captureError((action as { error: unknown }).error),
          toastService.error({
            duration: options.duration as number,
            buttons: options.buttons,
            ...options,
            message: sentryErrorHandler.extractMessage(
              (action as { error: unknown }).error,
              message,
            ),
          }),
        ]);
      });
    }),
  );
}

export function handleEndpointSuccess<
  ActionType extends ActionCreator = ActionCreator<string, (props: any) => Action>,
>(
  successAction: ActionType,
  options: HandleEndpointToastOptions<ActionType> = {
    message: 'De actie is succesvol opgeslagen.',
  },
): UnaryFunction<Observable<Action>, Observable<unknown>> {
  options = {
    duration: 3000,
    buttons: [{ text: 'Sluiten', role: 'cancel' }],
    position: 'bottom',
    ...options,
  };
  const toastService: ToastService = inject(ToastService);
  return pipe(
    ofType(successAction),
    tap((action: ReturnType<ActionType>) => {
      const message: string =
        typeof options.message === 'string' ? options.message : options.message(action);

      void toastService.create({
        duration: options.duration as number,
        buttons: options.buttons,
        ...options,
        message: message,
      });
    }),
  );
}
